import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { NFTContext } from "../context/NFTContext";
import { TransactionContext } from "../context/TransactionContext";

const PINATA_API_KEY = import.meta.env.VITE_PINATA_API_KEY;
const PINATA_SECRET_KEY = import.meta.env.VITE_PINATA_SECRET_KEY;

// Gateway & explorer URLs — configurable via .env, with sensible defaults.
// Set VITE_PINATA_GATEWAY_URL and VITE_ETHERSCAN_TX_BASE_URL in client/.env.
const PINATA_GATEWAY = import.meta.env.VITE_PINATA_GATEWAY_URL
  || "https://jade-immediate-koi-579.mypinata.cloud/ipfs/";
const ETHERSCAN_TX_BASE = import.meta.env.VITE_ETHERSCAN_TX_BASE_URL
  || "https://sepolia.etherscan.io/tx/";

const dataUrlToBlob = async (dataUrl) => {
  const res = await fetch(dataUrl);
  return await res.blob();
};

const getDataUrlImageDimensions = (dataUrl) =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth || 0, height: img.naturalHeight || 0 });
    img.onerror = () => resolve({ width: 0, height: 0 });
    img.src = dataUrl;
  });

const bufferToHex = (buffer) =>
  Array.from(new Uint8Array(buffer))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");

const sha256Blob = async (blob) => {
  if (!window.crypto?.subtle) return null;
  const buffer = await blob.arrayBuffer();
  const digest = await window.crypto.subtle.digest("SHA-256", buffer);
  return bufferToHex(digest);
};

const getDeviceSnapshot = () => {
  const nav = window.navigator;
  return {
    user_agent: nav.userAgent || "",
    platform: nav.platform || "",
    vendor: nav.vendor || "",
    language: nav.language || "",
    languages: Array.isArray(nav.languages) ? [...nav.languages] : [],
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "",
    timezone_offset_minutes: new Date().getTimezoneOffset(),
    hardware_concurrency: nav.hardwareConcurrency ?? null,
    device_memory_gb: nav.deviceMemory ?? null,
    max_touch_points: nav.maxTouchPoints ?? null,
    user_agent_data: nav.userAgentData
      ? {
          mobile: nav.userAgentData.mobile ?? null,
          platform: nav.userAgentData.platform ?? "",
          brands: Array.isArray(nav.userAgentData.brands) ? nav.userAgentData.brands : [],
        }
      : null,
    screen: {
      width: window.screen?.width ?? null,
      height: window.screen?.height ?? null,
      avail_width: window.screen?.availWidth ?? null,
      avail_height: window.screen?.availHeight ?? null,
      color_depth: window.screen?.colorDepth ?? null,
      pixel_depth: window.screen?.pixelDepth ?? null,
    },
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
      device_pixel_ratio: window.devicePixelRatio ?? 1,
    },
    browser_context: {
      secure_context: window.isSecureContext === true,
      origin: window.location?.origin || "",
    },
  };
};

const ipfsGatewayUrl = (cid) => `${PINATA_GATEWAY}${cid}`;
const etherscanTxUrl = (hash) => `${ETHERSCAN_TX_BASE}${hash}`;

// Short format for long hashes/CIDs: first 6 + last 6 chars.
const truncateMid = (s) => {
  if (!s || typeof s !== "string") return "N/A";
  return s.length <= 16 ? s : `${s.slice(0, 6)}\u2026${s.slice(-6)}`;
};

// Compact row showing a label + truncated/full value + [Open] [Copy] [Show full/short] actions.
const HashRow = ({ label, value, href, expanded, onToggle }) => {
  const display = expanded ? value : truncateMid(value);
  const openDisabled = !href;
  const copyDisabled = !value;
  const copyToClipboard = async () => {
    if (!value) return;
    try { await navigator.clipboard.writeText(value); } catch (e) { /* silent */ }
  };
  return (
    <div style={{
      padding: "8px 10px", borderRadius: 6,
      background: "rgba(15,23,42,0.55)", border: "1px solid #1e293b",
      display: "flex", flexDirection: "column", gap: "6px",
    }}>
      <div style={{
        fontSize: "0.68rem", color: "#94a3b8",
        textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700,
      }}>
        {label}
      </div>
      <div style={{
        display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap",
      }}>
        <code style={{
          flex: "1 1 auto", minWidth: 0,
          fontSize: "0.78rem", color: "#e2e8f0",
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          wordBreak: expanded ? "break-all" : "normal",
          overflow: expanded ? "visible" : "hidden",
          textOverflow: expanded ? "clip" : "ellipsis",
          whiteSpace: expanded ? "normal" : "nowrap",
          background: "rgba(30,41,59,0.6)",
          padding: "4px 8px", borderRadius: 4,
        }} title={value || ""}>
          {display}
        </code>
        <a
          href={href || "#"} target="_blank" rel="noopener noreferrer"
          onClick={(e) => { if (openDisabled) e.preventDefault(); }}
          style={{
            fontSize: "0.72rem", fontWeight: 700,
            padding: "4px 10px", borderRadius: 4,
            border: "1px solid #38bdf8",
            color: openDisabled ? "#475569" : "#7dd3fc",
            background: "rgba(56,189,248,0.1)",
            textDecoration: "none",
            pointerEvents: openDisabled ? "none" : "auto",
            opacity: openDisabled ? 0.5 : 1,
          }}
        >
          Open
        </a>
        <button
          type="button" onClick={copyToClipboard} disabled={copyDisabled}
          style={{
            fontSize: "0.72rem", fontWeight: 700,
            padding: "4px 10px", borderRadius: 4,
            border: "1px solid #64748b",
            color: copyDisabled ? "#475569" : "#cbd5e1",
            background: "rgba(100,116,139,0.1)",
            cursor: copyDisabled ? "not-allowed" : "pointer",
            opacity: copyDisabled ? 0.5 : 1,
          }}
        >
          Copy
        </button>
        <button
          type="button" onClick={onToggle} disabled={!value}
          style={{
            fontSize: "0.72rem", fontWeight: 700,
            padding: "4px 10px", borderRadius: 4,
            border: "1px solid #64748b",
            color: !value ? "#475569" : "#cbd5e1",
            background: "rgba(100,116,139,0.1)",
            cursor: !value ? "not-allowed" : "pointer",
            opacity: !value ? 0.5 : 1,
          }}
        >
          {expanded ? "Show short" : "Show full"}
        </button>
      </div>
    </div>
  );
};

const UploadMintPanel = ({
  frames,
  canUpload: canUploadProp = false,
  canMint: canMintProp = false,
  blockReason = "",
}) => {
  const { mintNFT, setCurrentCID } = useContext(NFTContext);
  const { currentAccount, connectWallet } = useContext(TransactionContext);

  const [imageCid, setImageCid] = useState(null);
  const [metadataCid, setMetadataCid] = useState(null);
  const [txHash, setTxHash] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isMinting, setIsMinting] = useState(false);
  const [error, setError] = useState(null);
  const [autoAttempted, setAutoAttempted] = useState(false);
  const [expandedRows, setExpandedRows] = useState({ image: false, metadata: false, tx: false });

  const toggleRow = (key) =>
    setExpandedRows((prev) => ({ ...prev, [key]: !prev[key] }));

  const framesOk = useMemo(() => Array.isArray(frames) && frames.length === 2, [frames]);
  const canUpload = useMemo(() => !!(canUploadProp && framesOk), [canUploadProp, framesOk]);
  const mintPrereqsOk = useMemo(() => !!(currentAccount && metadataCid), [currentAccount, metadataCid]);
  const canMint = useMemo(() => !!(canMintProp && mintPrereqsOk), [canMintProp, mintPrereqsOk]);
  const isWalletConnected = useMemo(() => !!currentAccount, [currentAccount]);
  const saveButtonDisabled = useMemo(
    () => !isWalletConnected || !canMint || isMinting,
    [isWalletConnected, canMint, isMinting],
  );
  const saveButtonLabel = useMemo(() => {
    if (!isWalletConnected) return "Connect Wallet to Save";
    if (isMinting) return "Saving...";
    return "Save on Blockchain";
  }, [isWalletConnected, isMinting]);

  const uploadToPinata = async () => {
    setError(null);
    if (!canUpload) {
      console.warn("Upload blocked:", blockReason);
      return;
    }
    if (!PINATA_API_KEY || !PINATA_SECRET_KEY) {
      setError("Missing VITE_PINATA_API_KEY / VITE_PINATA_SECRET_KEY");
      return;
    }

    setIsUploading(true);
    try {
      console.log("Pinata upload start");
      const uploadId = window.crypto?.randomUUID?.() || `deepreal-${Date.now()}`;
      const metadataCreatedAt = new Date();
      const metadataCreatedAtIso = metadataCreatedAt.toISOString();
      const metadataCreatedAtUnixMs = metadataCreatedAt.getTime();
      const blob = await dataUrlToBlob(frames[0]);
      const [frameDimensions, frameSha256] = await Promise.all([
        getDataUrlImageDimensions(frames[0]),
        sha256Blob(blob),
      ]);
      const fileName = `${uploadId}_frame1.jpg`;
      const file = new File([blob], fileName, { type: blob.type || "image/jpeg" });

      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post("https://api.pinata.cloud/pinning/pinFileToIPFS", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          pinata_api_key: PINATA_API_KEY,
          pinata_secret_api_key: PINATA_SECRET_KEY,
        },
      });

      console.log("Pinata upload response status: ", response.status);
      const cid = response.data?.IpfsHash;
      const imagePinnedAt = response.data?.Timestamp || new Date().toISOString();
      console.log("Pinata CID/URL: ", cid);

      if (!cid) throw new Error("Pinata response missing IpfsHash");
      setImageCid(cid);
      setCurrentCID?.(cid);

      const metadata = {
        name: `DeepReal Verified #${metadataCreatedAtUnixMs}`,
        description: "DeepReal - captured and verified via multi-stage mobile pipeline.",
        image: `ipfs://${cid}`,
        attributes: [
          { trait_type: "source", value: "DeepReal mobile flow" },
          { trait_type: "frame", value: "frame1" },
          { trait_type: "created_at_utc", value: metadataCreatedAtIso },
          { trait_type: "image_pinata_created_at", value: imagePinnedAt },
          { trait_type: "image_cid", value: cid },
          { trait_type: "upload_id", value: uploadId },
        ],
        properties: {
          schema: "deepreal.metadata.v1",
          created_at: metadataCreatedAtIso,
          created_at_unix_ms: metadataCreatedAtUnixMs,
          upload_id: uploadId,
          source: "DeepReal mobile flow",
          verification: {
            method: "AI and motion-based analysis",
            pipeline: "multi-stage mobile pipeline",
          },
          image_file: {
            cid,
            uri: `ipfs://${cid}`,
            gateway_url: ipfsGatewayUrl(cid),
            pinata_created_at: imagePinnedAt,
            frame: "frame1",
            file_name: fileName,
            mime_type: file.type,
            size_bytes: file.size,
            width: frameDimensions.width,
            height: frameDimensions.height,
            sha256: frameSha256,
          },
          device: getDeviceSnapshot(),
        },
      };

      const metaRes = await axios.post("https://api.pinata.cloud/pinning/pinJSONToIPFS", metadata, {
        headers: {
          "Content-Type": "application/json",
          pinata_api_key: PINATA_API_KEY,
          pinata_secret_api_key: PINATA_SECRET_KEY,
        },
      });

      console.log("Pinata upload response status: ", metaRes.status);
      const metaCid = metaRes.data?.IpfsHash;
      console.log("Pinata CID/URL: ", metaCid);
      if (!metaCid) throw new Error("Pinata JSON response missing IpfsHash");

      setMetadataCid(metaCid);
    } catch (e) {
      setError(e?.message || String(e));
      console.error("Pinata upload error:", e);
    } finally {
      setIsUploading(false);
    }
  };

  useEffect(() => {
    if (!canUpload) return;
    if (!framesOk) return;
    if (isUploading) return;
    if (metadataCid) return;
    if (autoAttempted) return;

    setAutoAttempted(true);
    uploadToPinata();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canUpload, framesOk, isUploading, metadataCid, autoAttempted]);

  const mint = async () => {
    setError(null);
    if (!canMintProp) {
      console.warn("Mint blocked:", blockReason);
      setError(blockReason || "Mint blocked.");
      return;
    }
    if (!canMint) return;

    setIsMinting(true);
    try {
      console.log("Mint start");
      const uri = `ipfs://${metadataCid}`;
      const tx = await mintNFT(currentAccount, uri);
      console.log("Mint tx hash: ", tx?.hash);
      setTxHash(tx?.hash || "unknown");
    } catch (e) {
      setError(e?.message || String(e));
      console.error("Mint error:", e);
    } finally {
      setIsMinting(false);
    }
  };

  return (
    <div className="dr-card dr-section">
      <h3 className="dr-title text-lg mb-2">Upload + Mint</h3>

      {!currentAccount && (
        <button className="dr-btn dr-primary mb-3" onClick={connectWallet}>
          Connect Wallet
        </button>
      )}

      {!canUpload ? (
        <div className="dr-callout mb-3" style={{ border: "1px solid #ef4444", color: "#fca5a5", background: "rgba(239,68,68,.12)" }}>
          Upload blocked: {blockReason || "Verification not passed."}
        </div>
      ) : null}

      {canUpload && isUploading ? (
        <div className="dr-callout mb-3" style={{ border: "1px solid #60a5fa", color: "#bfdbfe", background: "rgba(96,165,250,.12)" }}>
          Uploading...
        </div>
      ) : null}

      {canUpload && !isUploading && !metadataCid && error && (
        <button className="dr-btn dr-btn--proceed mb-3" onClick={uploadToPinata}>
          Retry Upload
        </button>
      )}

      <button
        className="dr-btn dr-btn--proceed mt-3"
        onClick={!saveButtonDisabled ? mint : undefined}
        disabled={saveButtonDisabled}
        title={!isWalletConnected ? "Connect your wallet first to save on blockchain." : undefined}
      >
        {saveButtonLabel}
      </button>

      {!canMintProp && blockReason ? (
        <div className="dr-callout mt-3" style={{ border: "1px solid #ef4444", color: "#fca5a5", background: "rgba(239,68,68,.12)" }}>
          Save disabled: {blockReason}
        </div>
      ) : null}
      {canMintProp && !metadataCid ? (
        <div className="dr-callout mt-3" style={{ border: "1px solid #f59e0b", color: "#fde68a", background: "rgba(245,158,11,.12)" }}>
          Save disabled: metadata missing (upload not complete).
        </div>
      ) : null}

      {error && (
        <div className="dr-error-toast mt-3">
          <span>{error}</span>
        </div>
      )}

      <div style={{ marginTop: "0.9rem", display: "flex", flexDirection: "column", gap: "0.5rem" }}>
        <HashRow
          label="Image on IPFS"
          value={imageCid}
          href={imageCid ? ipfsGatewayUrl(imageCid) : null}
          expanded={expandedRows.image}
          onToggle={() => toggleRow("image")}
        />
        <HashRow
          label="NFT Metadata"
          value={metadataCid}
          href={metadataCid ? ipfsGatewayUrl(metadataCid) : null}
          expanded={expandedRows.metadata}
          onToggle={() => toggleRow("metadata")}
        />
        {txHash && (
          <HashRow
            label="Blockchain transaction (Sepolia)"
            value={txHash}
            href={etherscanTxUrl(txHash)}
            expanded={expandedRows.tx}
            onToggle={() => toggleRow("tx")}
          />
        )}
      </div>
    </div>
  );
};

export default UploadMintPanel;
